/**
 * @module lib/commercial/keytar/MeteredWall
 */
import Wall from "./Wall";
import Meter from "./Meter";
import { getProp } from "utils/utils";
import { isSpotlight, isFreeToViewReferrer } from "lib/commercial/keytar/helpers";
import tgamConfig from "config/tgam";
import logger from "utils/logger";

const debugTag = "[METERED-WALL]";

/**
 * Created a metered Keytar wall
 * @class
 */
export default class MeteredWall extends Wall {
  /**
   * Creates an instance of MeteredWall.
   * @param {String} name - of the wall
   * @param {String} type - of the wall
   * @param {Object} [props={}] - of secondary property values
   * @param {Array<Object>} [props.rules] - various rules used to determine if wall is active
   * @param {Function} [props.suppressed] - optional custom logic that returns a Boolean indicating whether the wall should be suppressed
   * @param {String} [props.featureFlagName] - used to determine if wall is enabled via PB runtime property
   * @param {Object} [props.template] - that renders the Wall's UI markup
   * @param {String} [props.meterKey] - used for local storage id
   * @param {Number} [props.meterLimit] - of the amount of page-views the meter will allow a user, before it expires.
   * @param {Number} [props.meterRenew] - of the amount of days after which the meter count should reset.
   * @param {Object} [config=tgamConfig] - equivalent of tgamConfig
   */
  constructor(name, type, props = {}, config = tgamConfig) {
    super(name, type, {
      rules: props.rules,
      suppressed: props.suppressed,
      featureFlagName: props.featureFlagName,
      template: props.template,
      darwinTakeover: props.darwinTakeover,
    }, config);

    this.meter = new Meter({
      key: props.meterKey,
      limit: props.meterLimit,
      renew: props.meterRenew
    });
  }

  /**
   * Determine if the registration wall should be displayed to user.
   * @readonly
   * @returns {Boolean} true if user can bypass the wall.
   */
  get accessAllowed() {
    return this.matchedRule ? // is null when Hermes takes over
      this.matchedRule.accessAllowed({
        customer: this.customer,
        gam: this.tgam,
        meter: this.meter,
        paywallStatus: getProp(this.tgam, "datalayer.article.paywallStatus", "")
      }) : false;
  }

  /**
   * Process a pageview and determine if the user has access, or the content is free.
   * If the user doesn't have access and the content isn't free, then show the wall.
   * Only increment the meter count when content is not free.
   * @param {Boolean} [forceRender=false] - force render the wall
   * @param {Object} [analyticsData={}] - additional analytics data (e.g. Hermes analytics)
   * @param {String} contentId - for the viewed article
   * @param {String} paywallStatus - for the viewed article
   * @returns {Boolean<Promise>} true if user can bypass the wall.
   */
  pageview(forceRender = false, analyticsData = {}, contentId, paywallStatus) {
    // If hermes takes over, don't check access and increment the meter
    const isAllowed = forceRender ? false : this.accessAllowed;
    const isFree = this.isFreeContent(contentId);
    const hasAccess = !forceRender && (isAllowed || isFree);
    const shouldCount = getProp(this, "matchedRule.counter", () => true);

    if (!isFree && shouldCount({ paywallStatus })) {
      this.increment(contentId);
    }
    this.meter.log();

    if (hasAccess || !this.template) {
      return Promise.resolve(true);
    }
    return this.render().then(rendered => {
      rendered && this.analytics(Object.assign(
        this.template.additionalAnalytics,
        analyticsData
      ));
      return !rendered;
    });
  }

  /**
   * Determine if content is free (should not be metered).
   * @param {String} contentId - of the article
   * @returns {Boolean} true when free (no meter)
   */
  isFreeContent(contentId) {
    const rules = {
      isFreeArticle: this.meter.isFreeArticle(contentId),
      isSpotlight: this.isSpotlight(),
      isFreeToViewReferrer: this.isFreeToViewReferrer()
    };
    const free = Object.values(rules).some(item => item === true);
    logger.info(`${debugTag} isFreeContent:`, free, rules);
    return free;
  }

  /**
   * Increment the wall's meter count
   * @param {String} contentId - for the viewed article
   */
  increment(contentId) {
    const article = contentId || (window.location.origin + window.location.pathname); // ignore `search` values
    this.meter.increment(article);
  }

  /**
   * Reset the wall's meter count
   */
  reset() {
    this.meter.reset();
  }

  /**
   * Send the data analytics for this wall
   * @param {Object} shown - of extra analytics data
   * @returns {Object} of sent data
   */
  analytics(shown) {
    const data = Object.assign({
      meterCount: this.meter.count,
      meterLimit: this.meter.limit
    }, shown);
    return super.analytics(data);
  }

  /**
   * Check if the user was redirected to this article from Apple Spotlight (i.e., apple.news referrer)
   * @returns {Boolean}
   */
  isSpotlight() {
    return isSpotlight();
  }

  /**
   * Determine if the referrer is allows free to view content
   * @returns {Boolean}
   */
  isFreeToViewReferrer() {
    return isFreeToViewReferrer();
  }

}
